import {
  mutationOptions,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { ExpenseFields } from 'src/components/ExpenseForm/ExpenseForm'
import {
  ExpenseReportResponse,
  ExpenseResponse,
} from 'src/models/expenseReport.model'
import {
  approveExpenses,
  createExpense,
  deleteExpenses,
} from 'src/queries/expenses.network'
import {
  QueryError,
  recoverBackendErrorPayload,
} from 'src/queries/network.utils'
import { oopeQueryKeys } from 'src/queries/queries.constants'
import { CustomMutationOptions } from 'src/queries/queries.utils'
import {
  DELETE_EXPENSE_FAILURE,
  DELETE_EXPENSE_INIT,
  DELETE_EXPENSE_SUCCESS,
  POST_APPROVE_EXPENSES_SUCCESS,
} from 'src/redux/actions/data/expenses.actions'

export type ApproveExpensesParams = {
  expenseIds: string[]
}

export type DeleteExpenseParams = {
  expenseIds: string[]
}

// Named prefix, not the bare family root — sibling families aren't reachable from it (src/queries/AGENTS.md).
function invalidateOopeReportLists(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: oopeQueryKeys.approverLists() })
  queryClient.invalidateQueries({ queryKey: oopeQueryKeys.submitterLists() })
  queryClient.invalidateQueries({
    queryKey: oopeQueryKeys.directReportsLists(),
  })
}

export const expenseMutations = {
  createExpense: () =>
    mutationOptions<ExpenseResponse, QueryError, ExpenseFields>({
      mutationFn: (body) => createExpense(body),
    }),
  approveExpenses: () =>
    mutationOptions<ExpenseReportResponse, QueryError, ApproveExpensesParams>({
      mutationFn: ({ expenseIds }) => approveExpenses(expenseIds),
    }),
  deleteExpenses: () =>
    mutationOptions<ExpenseReportResponse, QueryError, DeleteExpenseParams>({
      mutationFn: ({ expenseIds }) => deleteExpenses(expenseIds),
    }),
}

export function useCreateExpense(
  options?: CustomMutationOptions<ExpenseResponse, QueryError, ExpenseFields>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...expenseMutations.createExpense(),
    onSuccess: (data, variables, onMutateResult, context) => {
      if (variables.expenseReportId) {
        queryClient.invalidateQueries({
          queryKey: oopeQueryKeys.reportDetails(variables.expenseReportId),
        })
      }
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useApproveExpenses(
  options?: CustomMutationOptions<
    ExpenseReportResponse,
    QueryError,
    ApproveExpensesParams
  >,
) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...expenseMutations.approveExpenses(),
    onSuccess: (data, variables, onMutateResult, context) => {
      dispatch({ type: POST_APPROVE_EXPENSES_SUCCESS, payload: data })
      queryClient.invalidateQueries({
        queryKey: oopeQueryKeys.reportDetails(data.id),
      })
      invalidateOopeReportLists(queryClient)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useDeleteExpense(
  options?: CustomMutationOptions<
    ExpenseReportResponse,
    QueryError,
    DeleteExpenseParams
  >,
) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { onMutate, onSuccess, onError, ...restOptions } = options ?? {}
  return useMutation({
    ...expenseMutations.deleteExpenses(),
    onMutate: (variables, context) => {
      dispatch({ type: DELETE_EXPENSE_INIT })
      return onMutate?.(variables, context)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      dispatch({ type: DELETE_EXPENSE_SUCCESS, payload: data })
      queryClient.invalidateQueries({
        queryKey: oopeQueryKeys.reportDetails(data.id),
      })
      invalidateOopeReportLists(queryClient)
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      dispatch({
        type: DELETE_EXPENSE_FAILURE,
        payload: recoverBackendErrorPayload(error),
      })
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
